/* eslint-disable lyrahealth/no-react-native-text */
import React from 'react'
import { type IntlShape } from 'react-intl'

import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import { isNil } from 'lodash-es'

import { CDN_ASSETS_LINK_BASE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { lightThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { formatDOB } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'

import { ClientProfileCardPDF } from './ClientProfileCardPDF'
import { type RecordsPdfAssignmentsSession, type RecordsPdfSection } from './recordsPdfSections'

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: 80,
    paddingBottom: 60,
    paddingHorizontal: 30,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  pageHeader: {
    position: 'absolute',
    top: 20,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 10,
    backgroundColor: '#FFFFFF',
  },
  logo: {
    width: 'auto',
    height: 30,
  },
  pageHeaderRight: {
    alignItems: 'flex-end',
  },
  pageHeaderText: {
    fontSize: 10,
    color: '#4F4D4A',
    marginBottom: 2,
  },
  pageFooter: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#01798D',
    paddingTop: 10,
  },
  pageNumber: {
    fontSize: 10,
    color: '#4F4D4A',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1A1918',
    textAlign: 'center',
  },
  text: {
    fontSize: 12,
    lineHeight: 1.5,
    marginBottom: 8,
    color: '#333333',
  },
  noContent: {
    fontStyle: 'italic',
    color: '#666666',
    textAlign: 'center',
    padding: 20,
  },
  sectionTitlesContainer: {
    marginTop: 40,
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: 18,
    color: lightThemeColors.textActive,
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.textActive,
    marginBottom: 20,
    paddingBottom: 5,
  },
  sectionContentTitle: {
    fontSize: 16,
    color: '#1a1918',
    marginTop: 24,
  },
  episodeTitle: {
    fontWeight: 'bold',
    fontSize: 18,
    color: lightThemeColors.textActive,
    borderBottomWidth: 1,
    borderBottomColor: lightThemeColors.textActive,
    marginBottom: 20,
    paddingBottom: 5,
  },
})

interface ClientRecordsExportPDFProps {
  clientDetails: ClientObject
  intl: IntlShape
  clientSummaryPDFContent: React.ReactNode
  notesPDFContent?: { [key: string]: React.ReactNode }
  programName?: string
  allSections?: boolean
  benefitsSummarySection?: boolean
  sessionsSection?: boolean
  notesSection?: boolean
  progressSection?: boolean
  assignmentsSection?: boolean
  medicationsSection?: boolean
  providerName?: string
  sessionsPDFContent?: { [key: string]: React.ReactNode }
  clientProgressPDFContent?:
    | {
        episodeName: string
        progressByEpisode: React.ReactNode
      }[]
    | undefined
  clientAssignmentsPDFContent?:
    | {
        episodeName: string
        assignmentsBySession: RecordsPdfAssignmentsSession[]
      }[]
    | undefined
    | null
  medicationsPDFContent?: React.ReactNode
  isEngageDAProvider?: boolean
}

const PageHeader: React.FC<{
  clientDetails: ClientObject
  providerName?: string
}> = ({ clientDetails, providerName }) => (
  <View style={styles.pageHeader} fixed>
    <Image style={styles.logo} src={`${CDN_ASSETS_LINK_BASE}/lyra_logo.png`} />
    <View style={styles.pageHeaderRight}>
      <Text style={styles.pageHeaderText}>
        Client: {clientDetails?.first_name} {clientDetails?.last_name}
      </Text>
      <Text style={styles.pageHeaderText}>Email: {clientDetails?.email}</Text>
      <Text style={styles.pageHeaderText}>DOB: {formatDOB(clientDetails.date_of_birth)}</Text>
      <Text style={styles.pageHeaderText}>Provider: {providerName || 'Unknown Provider'}</Text>
    </View>
  </View>
)

const PageFooter: React.FC<{ prefix?: string }> = ({ prefix }) => (
  <View style={styles.pageFooter} fixed>
    <Text
      style={styles.pageNumber}
      render={({ pageNumber, totalPages }) => `${prefix ? prefix + ' - ' : ''}Page ${pageNumber} of ${totalPages}`}
    />
  </View>
)

const SectionTitles: React.FC<
  Omit<ClientRecordsExportPDFProps, 'clientDetails' | 'intl' | 'programName' | 'providerName'>
> = ({
  clientSummaryPDFContent,
  sessionsPDFContent,
  notesPDFContent,
  clientProgressPDFContent,
  clientAssignmentsPDFContent,
  medicationsPDFContent,
  allSections = true,
  benefitsSummarySection = false,
  sessionsSection = false,
  notesSection = false,
  progressSection = false,
  assignmentsSection = false,
  medicationsSection = false,
}) => (
  <View style={styles.sectionTitlesContainer}>
    <Text style={styles.sectionTitle}>Document Sections</Text>
    <Text style={styles.sectionContentTitle}>Client Profile</Text>
    {(allSections || benefitsSummarySection) && clientSummaryPDFContent && (
      <Text style={styles.sectionContentTitle}>Benefits Summary</Text>
    )}
    {(allSections || sessionsSection) && sessionsPDFContent && <Text style={styles.sectionContentTitle}>Sessions</Text>}
    {(allSections || notesSection) && notesPDFContent && (
      <>
        <Text style={styles.sectionContentTitle}>Notes Overview</Text>
        <Text style={styles.sectionContentTitle}>Session Notes</Text>
        <Text style={styles.sectionContentTitle}>General Notes</Text>
        <Text style={styles.sectionContentTitle}>Referral Notes</Text>
      </>
    )}
    {(allSections || progressSection) && clientProgressPDFContent && (
      <Text style={styles.sectionContentTitle}>Client Progress</Text>
    )}
    {(allSections || assignmentsSection) && clientAssignmentsPDFContent && (
      <Text style={styles.sectionContentTitle}>Assignments</Text>
    )}
    {(allSections || medicationsSection) && medicationsPDFContent && (
      <Text style={styles.sectionContentTitle}>Lyra Medications</Text>
    )}
  </View>
)

export const ClientRecordsExportPDF = ({
  clientDetails,
  intl,
  clientSummaryPDFContent,
  notesPDFContent,
  programName = 'Unknown Program',
  providerName = 'Unknown Provider',
  sessionsPDFContent,
  clientProgressPDFContent,
  clientAssignmentsPDFContent,
  medicationsPDFContent,
  allSections = true,
  sessionsSection = false,
  notesSection = false,
  progressSection = false,
  assignmentsSection = false,
  medicationsSection = false,
  benefitsSummarySection = false,
  isEngageDAProvider,
}: ClientRecordsExportPDFProps): RecordsPdfSection[] => {
  const sections: RecordsPdfSection[] = [
    {
      sectionKey: 'tableOfContents',
      sectionName: 'Table of Contents',
      content: (
        <Document key='client-records-contents'>
          <Page size='A4' style={styles.page}>
            <PageHeader clientDetails={clientDetails} providerName={providerName} />
            <Text style={styles.headerTitle}>Client Records</Text>
            <SectionTitles
              clientSummaryPDFContent={clientSummaryPDFContent}
              sessionsPDFContent={sessionsPDFContent}
              notesPDFContent={notesPDFContent}
              clientProgressPDFContent={clientProgressPDFContent}
              clientAssignmentsPDFContent={clientAssignmentsPDFContent}
              medicationsPDFContent={medicationsPDFContent}
              allSections={allSections}
              benefitsSummarySection={benefitsSummarySection}
              sessionsSection={sessionsSection}
              notesSection={notesSection}
              progressSection={progressSection}
              assignmentsSection={assignmentsSection}
              medicationsSection={medicationsSection}
            />
          </Page>
        </Document>
      ),
    },
  ]
  sections.push({
    sectionKey: 'clientDetails',
    sectionName: 'Client Details',
    content: (
      <Document key='client-records-profile'>
        <Page size='A4' style={styles.page}>
          <PageHeader clientDetails={clientDetails} providerName={providerName} />
          <PageFooter prefix='Client Profile' />
          <ClientProfileCardPDF
            clientDetails={clientDetails}
            intl={intl}
            programName={programName}
            isEngageDAProvider={isEngageDAProvider}
          />
        </Page>
      </Document>
    ),
  })
  if ((allSections || benefitsSummarySection) && clientSummaryPDFContent) {
    sections.push({
      sectionKey: 'benefitsSummary',
      sectionName: 'Client Summary',
      content: (
        <Document key='client-records-summary'>
          <Page size='A4' style={styles.page}>
            <PageHeader clientDetails={clientDetails} providerName={providerName} />
            <PageFooter prefix='Client Summary' />
            {clientSummaryPDFContent}
          </Page>
        </Document>
      ),
    })
  }
  if ((allSections || sessionsSection) && sessionsPDFContent) {
    Object.entries(sessionsPDFContent).forEach(([episodeName, content], indexOf) => {
      if (content) {
        sections.push({
          sectionKey: 'sessions',
          sectionName: `Client Sessions for ${episodeName}`,
          content: (
            <Document key={`client-records-sessions-${indexOf}`}>
              <Page size='A4' style={styles.page}>
                <PageHeader clientDetails={clientDetails} providerName={providerName} />
                <PageFooter prefix={`Sessions for ${episodeName}`} />
                <Text style={styles.sectionTitle}>Sessions for {episodeName}</Text>
                {content}
              </Page>
            </Document>
          ),
        })
      }
    })
  }

  if ((allSections || notesSection) && !isNil(notesPDFContent)) {
    Object.entries(notesPDFContent).forEach(([episodeName, content], indexOf) => {
      if (!isNil(content)) {
        sections.push({
          sectionKey: 'notes',
          sectionName: `Client Notes for ${episodeName}`,
          content: (
            <Document key={`client-records-notes-${indexOf}`}>
              <Page size='A4' style={styles.page}>
                <PageHeader clientDetails={clientDetails} providerName={providerName} />
                <PageFooter prefix={`Notes for ${episodeName}`} />
                <Text style={styles.sectionTitle}>Notes for {episodeName}</Text>
                {content}
              </Page>
            </Document>
          ),
        })
      }
    })
  }

  if ((allSections || progressSection) && !isNil(clientProgressPDFContent)) {
    clientProgressPDFContent.forEach(({ episodeName, progressByEpisode }) => {
      if (!isNil(progressByEpisode)) {
        sections.push({
          sectionKey: 'progress',
          sectionName: `Client Progress for ${episodeName}`,
          content: (
            <Document key={`client-records-progress-${episodeName}`}>
              <Page size='A4' style={styles.page}>
                <PageHeader clientDetails={clientDetails} providerName={providerName} />
                <PageFooter prefix={`Progress for ${episodeName}`} />
                <Text style={styles.sectionTitle}>Progress for {episodeName}</Text>
                {progressByEpisode}
              </Page>
            </Document>
          ),
        })
      }
    })
  }

  if ((allSections || assignmentsSection) && !isNil(clientAssignmentsPDFContent)) {
    clientAssignmentsPDFContent.forEach(({ episodeName, assignmentsBySession }) => {
      if (assignmentsBySession) {
        assignmentsBySession.forEach(({ sectionKey, sessionName, content }) => {
          sections.push({
            sectionKey,
            sectionName: `Client Assignments for ${episodeName} - ${sessionName}`,
            content: (
              <Document key={`client-records-assignments-${sessionName}`}>
                <Page size='A4' style={styles.page}>
                  <PageHeader clientDetails={clientDetails} providerName={providerName} />
                  <PageFooter prefix={`Assignments for ${episodeName} - ${sessionName}`} />
                  <Text style={styles.sectionTitle}>
                    Assignments for {episodeName} - {sessionName}
                  </Text>
                  {content}
                </Page>
              </Document>
            ),
          })
        })
      }
    })
  }

  if ((allSections || medicationsSection) && !isNil(medicationsPDFContent)) {
    sections.push({
      sectionKey: 'medications',
      sectionName: 'Client Medications',
      content: (
        <Document key='client-records-medications'>
          <Page size='A4' style={styles.page}>
            <PageHeader clientDetails={clientDetails} providerName={providerName} />
            <PageFooter prefix='Medications' />
            {medicationsPDFContent}
          </Page>
        </Document>
      ),
    })
  }

  return sections
}
